(() => {
  /** @type {any} */
  const windowAny = window;

  /** @type {any} */
  const utils = (windowAny['EcommRecommendationsUtils'] = windowAny['EcommRecommendationsUtils'] || {});

  /** @type {number} */
  let detectActivePersonToggleRafId = 0;

  /**
   * Coalesce rapid `forceDetect` calls into one frame.
   * @param {HTMLElement} toggleOptionsElement
   * @param {{ source?: string }} [options]
   */
  function scheduleDetectActivePersonToggle(toggleOptionsElement, options = {}) {
    if (!(toggleOptionsElement instanceof HTMLElement)) return;
    if (detectActivePersonToggleRafId) return;
    detectActivePersonToggleRafId = requestAnimationFrame(() => {
      detectActivePersonToggleRafId = 0;
      try {
        utils.detectActivePersonToggle(toggleOptionsElement, options);
      } catch {
        /* ignore */
      }
    });
  }

  /**
   * Detect which person toggle is currently active.
   * @param {HTMLElement} toggleOptionsElement
   * @param {{ source?: string }} [options]
   */
  utils.detectActivePersonToggle = function detectActivePersonToggle(toggleOptionsElement, options = {}) {
    const checkedInput = toggleOptionsElement.querySelector('input[name="ecomm-profile"]:checked');
    if (!(checkedInput instanceof HTMLInputElement)) {
      return false;
    }

    const label = toggleOptionsElement.querySelector(`label[for="${checkedInput.id}"]`);
    const displayName = label instanceof HTMLElement ? label.textContent?.trim() : checkedInput.value;

    // Dispatch event to ecomm-recommendations-guest.js
    try {
      window.dispatchEvent(new CustomEvent('ecomm-profile-changed', {
        detail: {
          profileKey: checkedInput.value,
          displayName: displayName,
          id: checkedInput.id,
          source: options.source || 'change',
        }
      }));
      return true;
    } catch (error) {
      // console.warn('[ECOMM-RECOMMENDATIONS] Failed to dispatch ecomm-profile-changed', error);
      return false;
    }
  };

  /**
   * Initialize person toggle change listener to log active toggle.
   * This should be called once per toggle options element.
   * @param {HTMLElement} toggleOptionsElement
   * @param {{ forceDetect?: boolean }} [options]
   */
  utils.initPersonToggleLogger = function initPersonToggleLogger(toggleOptionsElement, options = {}) {
    if (!(toggleOptionsElement instanceof HTMLElement)) return;

    // Prevent duplicate listeners
    if (toggleOptionsElement.dataset.personToggleLoggerInitialized === '1') {
      if (options.forceDetect) {
        scheduleDetectActivePersonToggle(toggleOptionsElement, { source: 'forced' });
      }
      return;
    }
    toggleOptionsElement.dataset.personToggleLoggerInitialized = '1';

    // Log initial state
    utils.detectActivePersonToggle(toggleOptionsElement, { source: 'initial' });

    // Listen for changes
    toggleOptionsElement.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.name !== 'ecomm-profile') return;

      utils.detectActivePersonToggle(toggleOptionsElement, { source: 'change' });
    });
  };

  /**
   * Wire every person-toggle radiogroup so `change` dispatches `ecomm-profile-changed`.
   * Safe to call repeatedly (idempotent per node). Call after guest/logged-in listeners exist.
   */
  utils.ensurePersonToggleRecommendationDispatchBound = function ensurePersonToggleRecommendationDispatchBound() {
    document.querySelectorAll('[data-ecomm-person-toggle-options]').forEach((/** @type {Element} */ n) => {
      if (n instanceof HTMLElement) utils.initPersonToggleLogger(n, { forceDetect: true });
    });
  };

  /**
   * Read + parse JSON embedded in a script tag (or any element) matched by selector.
   * Shared by guest + logged-in controllers.
   *
   * @param {string} selector
   * @param {string=} logLabel
   * @returns {any|null}
   */
  utils.readJsonFromScriptTag = function readJsonFromScriptTag(selector, logLabel) {
    const el = document.querySelector(selector);
    if (!(el instanceof HTMLElement)) return null;
    const raw = String(el.textContent || '').trim();
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      // console.warn(`${String(logLabel || '[ECOMM]')} failed to parse embedded JSON`, { selector, error });
      return null;
    }
  };

  /**
   * Convert Shopify product GID to numeric product id string.
   * Example: gid://shopify/Product/123 -> "123"
   *
   * Shared by guest + logged-in.
   *
   * @param {string} recommendationProductId
   * @returns {string|null}
   */
  utils.convertRecommendationProductIdToId = utils.convertRecommendationProductIdToId || function convertRecommendationProductIdToId(recommendationProductId) {
    const match = String(recommendationProductId || '').match(/\/(\d+)\s*$/);
    return match?.[1] || null;
  };

  /**
   * Parse portion size from variant-title-like text.
   *
   * Supports:
   * - soups: "halve liter" => 0.5, "hele liter" => 1
   * - liters numeric: "0,5 liter", "1 liter", "0.5l"
   * - grams: "400g", "400gr", "400 gram", "400grams"
   *
   * Note: For historical reasons we call this `portionGrams` in multiple places,
   * but for soups this value represents liters (0.5 / 1), not grams.
   *
   * @param {string} text
   * @returns {number|null}
   */
  utils.parsePortionSizeFromText = utils.parsePortionSizeFromText || function parsePortionSizeFromText(text) {
    const raw = String(text || '').toLowerCase();

    // Soups: explicit labels
    if (raw.includes('halve') && raw.includes('liter')) return 0.5;
    if (raw.includes('hele') && raw.includes('liter')) return 1;

    // Liters numeric (e.g. "0,5 liter", "1 liter", "0.5l")
    const litersMatch = raw.match(/(\d+(?:[.,]\d+)?)\s*(?:l|liter)\b/);
    if (litersMatch?.[1]) {
      const liters = Number(String(litersMatch[1] || '').replace(',', '.'));
      return Number.isFinite(liters) ? liters : null;
    }

    // Grams (e.g. "400g", "400gr", "400 gram", "400grams")
    const gramsMatch = raw.match(/(\d+(?:[.,]\d+)?)\s*(?:g|gr|gram|grams)\b/);
    if (!gramsMatch?.[1]) return null;
    const grams = Number(String(gramsMatch[1] || '').replace(',', '.'));
    return Number.isFinite(grams) ? grams : null;
  };

  /**
   * Compare two portion values. Handles liters (0.5/1) and grams (400/550) safely.
   * @param {number|null} a
   * @param {number|null} b
   * @returns {boolean}
   */
  utils.isSamePortionSize = utils.isSamePortionSize || function isSamePortionSize(a, b) {
    if (!Number.isFinite(Number(a)) || !Number.isFinite(Number(b))) return false;
    const aa = Number(a);
    const bb = Number(b);
    // Liters are typically < 10;
    if (aa < 10 || bb < 10) return Math.abs(aa - bb) < 0.01;
    return Math.round(aa) === Math.round(bb);
  };

  /**
   * Read churned recommendation ordered product IDs for a profile key from sessionStorage.
   * Used as a "priority ordering" input (stable tiebreaking) by both guest + logged-in.
   *
   * @param {string} profileKey
   * @param {string=} logLabel
   * @returns {string[]}
   */
  utils.readChurnedRecommendationOrderedIds = function readChurnedRecommendationOrderedIds(profileKey, logLabel) {
    const churnedRecommendationsRaw = sessionStorage.getItem('churned_recommendations');
    if (!churnedRecommendationsRaw) return [];

    /** @type {any|null} */
    let churnedRecommendations = null;
    try {
      churnedRecommendations = JSON.parse(churnedRecommendationsRaw);
    } catch (error) {
      // console.warn(`${String(logLabel || '[ECOMM]')} failed to parse churned_recommendations from sessionStorage`, error);
      return [];
    }

    const churnedProfiles = Array.isArray(churnedRecommendations?.profiles) ? churnedRecommendations.profiles : [];
    const requestedProfileKey = String(profileKey || '').trim();
    const requestedProfile =
      churnedProfiles.find((/** @type {any} */ p) => String(p?.profileKey || '').trim() === requestedProfileKey) || null;

    // If churned recs don't exist for the requested profile (e.g. p2/p3),
    // fall back to p1 churned recs if available.
    const fallbackProfile =
      requestedProfileKey && requestedProfileKey !== 'p1'
        ? churnedProfiles.find((/** @type {any} */ p) => String(p?.profileKey || '').trim() === 'p1') || null
        : null;

    const requestedItems = Array.isArray(requestedProfile?.recommendations) ? requestedProfile.recommendations : [];
    const fallbackItems = Array.isArray(fallbackProfile?.recommendations) ? fallbackProfile.recommendations : [];

    /** @type {any[]} */
    const churnedItems =
      requestedItems.length
        ? requestedItems
        : (fallbackItems.length ? fallbackItems : []);

    if (!requestedItems.length && fallbackItems.length) {
      console.log(`${String(logLabel || '[ECOMM]')} using p1 churned recommendations as fallback`, {
        requestedProfileKey,
        fallbackProfileKey: 'p1',
        fallbackCount: fallbackItems.length
      });
    }

    /** @type {string[]} */
    const orderedChurnedRecommendationProductIds = [];
    const seenIds = new Set();
    churnedItems.forEach((/** @type {any} */ churnedItem) => {
      const id = utils.convertRecommendationProductIdToId(String(churnedItem?.productId || '').trim());
      if (!id) return;
      if (seenIds.has(id)) return;
      seenIds.add(id);
      orderedChurnedRecommendationProductIds.push(id);
    });

    return orderedChurnedRecommendationProductIds;
  };

  /**
   * Parse numeric portion from a survey profile's `portionSize.size` (same field as meal rows).
   *
   * @param {any} profile
   * @returns {number|null}
   */
  utils.parseProfilePortionGramsFromSurveyProfile = function parseProfilePortionGramsFromSurveyProfile(profile) {
    const raw = profile?.portionSize?.size;
    const n = Number(String(raw ?? '').trim());
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  /**
   * Build the algorithm picker context (prefs + display info) from a survey-style payload.
   * Payload shape is the same for:
   * - guest sessionStorage('recommendations')
   * - logged-in customer metafield 'mijn_gegevens' embedded as JSON
   *
   * This keeps guest + logged-in behavior consistent.
   *
   * @param {any} payload
   * @param {string} profileKey
   * @returns {{
   *  profile: any|null,
   *  selectedDietLabels: string[],
   *  preferredDietOrderTokens: string[],
   *  prefs: { dietTokens: string[], kitchenTokens: string[], portionGrams: null, mealSlots: { dietToken: string, portionGrams: number|null }[], soupSlots: { portionGrams: number|null }[], mealsCount: number, soupCount: number, dessertCount: number }
   * }|null}
   */
  utils.buildThuysVersRecommendationPickerContextFromPayload = function buildThuysVersRecommendationPickerContextFromPayload(payload, profileKey) {
    /** @type {any} */
    const algorithmApi = windowAny.EcommRecommendationsAlgorithm;
    if (!algorithmApi || typeof algorithmApi.convertMetafieldValuesToNormalizedTokens !== 'function') return null;

    const profiles = Array.isArray(payload?.profiles) ? payload.profiles : [];
    const profile =
      profiles.find((/** @type {any} */ p) => String(p?.profileKey || '').trim() === String(profileKey || '').trim()) || null;
    if (!profile) return null;

    const addOns = Array.isArray(payload?.addOns) ? payload.addOns : [];
    const soupCount = addOns.reduce((/** @type {number} */ sum, /** @type {any} */ addOn) => {
      if (String(addOn?.addonType || '').toLowerCase() !== 'soup') return sum;
      const qty = Number(addOn?.quantity || 0);
      return sum + (Number.isFinite(qty) ? qty : 0);
    }, 0);
    const dessertCount = addOns.reduce((/** @type {number} */ sum, /** @type {any} */ addOn) => {
      if (String(addOn?.addonType || '').toLowerCase() !== 'dessert') return sum;
      const qty = Number(addOn?.quantity || 0);
      return sum + (Number.isFinite(qty) ? qty : 0);
    }, 0);

    /**
     * Soup size slots come from addOns, e.g.
     * { addonType: "soup", quantity: 1, options: { size: "0.5", unit: "liters" } }
     *
     * We keep using `portionGrams` as the field name for historical reasons in the algorithm,
     * but for soups this value is liters (0.5 / 1), not grams.
     *
     * @type {{ portionGrams: number|null }[]}
     */
    const soupSlots = addOns
      .filter((/** @type {any} */ addOn) => String(addOn?.addonType || '').toLowerCase() === 'soup')
      .flatMap((/** @type {any} */ addOn) => {
        const qty = Number(addOn?.quantity || 0);
        const unit = String(addOn?.options?.unit || '').trim().toLowerCase();
        const sizeRaw = addOn?.options?.size;
        const size = Number(String(sizeRaw ?? '').trim().replace(',', '.'));
        const liters = (unit.includes('liter') || unit === 'l') && Number.isFinite(size) && size > 0 ? size : null;
        const count = Number.isFinite(qty) && qty > 0 ? Math.floor(qty) : 0;
        return Array.from({ length: count }, () => ({ portionGrams: liters }));
      });

      // console.log('[ECOMM][SOUP SLOTS]', soupSlots);

    const dietTypeEntries = Array.isArray(profile?.dietType) ? profile.dietType : [];
    const diets = dietTypeEntries.map((/** @type {any} */ d) => d?.diet);
    const kitchens = Array.isArray(profile?.typeOfKitchen) ? profile.typeOfKitchen : [];
    const mealsCount = Number(profile?.mealsPerWeek || profile?.mealsCount || 0) || 0;

    /** @type {{ dietToken: string, portionGrams: number|null }[]} */
    const mealSlots = dietTypeEntries
      .slice(0, mealsCount || dietTypeEntries.length)
      .map((/** @type {any} */ entry) => {
        const dietToken = String(entry?.diet || '').trim().toLowerCase();
        const gramsRaw = entry?.portionSize?.size;
        const grams = Number(String(gramsRaw ?? '').trim());
        return { dietToken, portionGrams: Number.isFinite(grams) && grams > 0 ? grams : null };
      })
      .filter((/** @type {any} */ slot) => Boolean(slot.dietToken));

    const preferredDietOrderTokens =
      typeof algorithmApi.getDietPreferenceOrderTokens === 'function'
        ? algorithmApi.getDietPreferenceOrderTokens(dietTypeEntries)
        : [];

    const selectedDietLabels = Array.from(
      new Set(
        (Array.isArray(profile?.dietType) ? profile.dietType : [])
          .map((/** @type {any} */ d) => String(d?.diet || '').trim())
          .filter(Boolean)
      )
    );

    const prefs = {
      dietTokens: algorithmApi.convertMetafieldValuesToNormalizedTokens(diets),
      kitchenTokens: algorithmApi.convertMetafieldValuesToNormalizedTokens(kitchens),
      portionGrams: null,
      mealSlots,
      soupSlots,
      mealsCount,
      soupCount,
      dessertCount,
    };

    return { profile, selectedDietLabels, preferredDietOrderTokens, prefs };
  };

  /**
   * Apply a stable "priority ordering" to elements based on an ordered list of product ids.
   * Elements are matched using `data-product-id`.
   *
   * - Matched items come first, in `orderedProductIds` order
   * - Remaining items keep their original order
   *
   * @param {HTMLElement[]} elements
   * @param {string[]} orderedProductIds
   * @returns {HTMLElement[]}
   */
  utils.prioritizeElementsByProductId = function prioritizeElementsByProductId(elements, orderedProductIds) {
    const items = Array.isArray(elements) ? elements.filter((el) => el instanceof HTMLElement) : [];
    const ids = Array.isArray(orderedProductIds) ? orderedProductIds : [];
    if (!items.length || !ids.length) return items;

    /** @type {HTMLElement[]} */
    const prioritized = [];
    const used = new Set();

    ids.forEach((/** @type {string} */ id) => {
      const match = items.find((el) => String(el.getAttribute('data-product-id') || '').trim() === String(id));
      if (!match) return;
      if (used.has(match)) return;
      used.add(match);
      prioritized.push(match);
    });

    const rest = items.filter((el) => !used.has(el));
    return [...prioritized, ...rest];
  };

  /**
   * Sort elements by preferred diet order (stable), using `data-ecomm-diets`.
   * Items that don't match any preferred token are pushed to the end.
   *
   * @param {HTMLElement[]} elements
   * @param {string[]} preferredDietOrderTokens
   * @returns {HTMLElement[]}
   */
  utils.sortElementsByPreferredDiet = function sortElementsByPreferredDiet(elements, preferredDietOrderTokens) {
    /** @type {any} */
    const algorithmApi = windowAny.EcommRecommendationsAlgorithm;
    if (!algorithmApi || typeof algorithmApi.convertMetafieldValuesToNormalizedTokens !== 'function') return elements;

    const items = Array.isArray(elements) ? elements.filter((el) => el instanceof HTMLElement) : [];
    const order = Array.isArray(preferredDietOrderTokens) ? preferredDietOrderTokens : [];
    if (!items.length || !order.length) return items;

    /** @param {HTMLElement} el @returns {number} */
    const resolveDietBucketIndex = (el) => {
      const rawDiets = el.getAttribute('data-ecomm-diets');
      if (!rawDiets) return Number.POSITIVE_INFINITY;
      let parsed = null;
      try { parsed = JSON.parse(rawDiets); } catch { parsed = rawDiets; }
      const dietTokens = algorithmApi.convertMetafieldValuesToNormalizedTokens(parsed);
      for (let i = 0; i < order.length; i++) {
        if (dietTokens.includes(order[i])) return i;
      }
      return Number.POSITIVE_INFINITY;
    };

    return items
      .map((el, idx) => ({ el, idx, bucket: resolveDietBucketIndex(el) }))
      .sort((a, b) => (a.bucket - b.bucket) || (a.idx - b.idx))
      .map((x) => x.el);
  };

  /**
   * Apply a picker result to DOM list items:
   * - set/clear `data-ecomm-recommendation-picked`
   * - set/clear `data-ecomm-selected-diet-token` based on chosenDietByElement
   * - compute a final ordered list: chosen first, then remaining sorted by preferred diet
   *
   * Shared by guest + logged-in.
   *
   * @param {HTMLElement[]} allItems
   * @param {{ orderedChosen: HTMLElement[], orderedRest: HTMLElement[], chosenDietByElement?: { element: HTMLElement, dietToken: string }[] }} pickResult
   * @param {string[]} preferredDietOrderTokens
   * @param {number|null|undefined} profilePortionGramsForRest — survey `portionSize` applied to non-recommended (orderedRest) cards only; recommended picks unchanged.
   * @returns {{ orderedItems: HTMLElement[], pickedProductIdSet: Set<string> }}
   */
  utils.applyPickerResultToRecommendItems = function applyPickerResultToRecommendItems(allItems, pickResult, preferredDietOrderTokens, profilePortionGramsForRest) {
    const items = Array.isArray(allItems) ? allItems.filter((el) => el instanceof HTMLElement) : [];
    const chosen = Array.isArray(pickResult?.orderedChosen) ? pickResult.orderedChosen : [];
    const rest = Array.isArray(pickResult?.orderedRest) ? pickResult.orderedRest : [];

    const chosenSet = new Set(chosen);
    items.forEach((el) => {
      if (chosenSet.has(el)) el.dataset.ecommRecommendationPicked = 'recommendedProduct';
      else delete el.dataset.ecommRecommendationPicked;
    });

    const chosenDietMap = new Map(
      Array.isArray(pickResult?.chosenDietByElement)
        ? pickResult.chosenDietByElement.map((/** @type {any} */ x) => [x.element, x.dietToken])
        : []
    );

    const chosenPortionGramsMap = new Map(
      Array.isArray((/** @type {any} */ (pickResult))?.chosenPortionGramsByElement)
        ? (/** @type {any} */ (pickResult)).chosenPortionGramsByElement.map((/** @type {any} */ x) => [x.element, Number(x.portionGrams)])
        : []
    );

    /**
     * Find the variant dropdown inside a recommendation list item.
     * @param {HTMLElement} listItem
     */
    const findVariantDropdown = (listItem) => {
      const productId = String(listItem.getAttribute('data-product-id') || '').trim();
      const productCard = listItem.closest('product-card') || listItem.querySelector('product-card');
      const dropdown = productCard?.querySelector(`variant-select-dropdown[data-product-id="${productId}"]`);
      return dropdown instanceof HTMLElement ? dropdown : null;
    };

    /**
     * First menu index whose title matches `portionGrams`, or -1.
     *
     * @param {HTMLElement} dropdown
     * @param {number} portionGrams
     * @returns {number}
     */
    const findMatchingPortionVariantIndex = (dropdown, portionGrams) => {
      const menuItems = Array.from(dropdown.querySelectorAll('.dropdown-menu-item[data-variant-title]'))
        .filter((el) => el instanceof HTMLElement);
      return menuItems.findIndex((menuEl) => {
        if (String(menuEl.dataset?.variantAvailable || '') === 'false') return false;
        const title = String(menuEl.dataset?.variantTitle || '');
        const parsed = utils.parsePortionSizeFromText(title);
        return parsed !== null && utils.isSamePortionSize(parsed, Number(portionGrams));
      });
    };

    /**
     * Find + select the first available variant option that matches a grams value.
     * We intentionally do this here (not in the dropdown component) so the component stays generic.
     * Call sites that only need to know if a match exists can use the boolean return (e.g. orderedRest).
     *
     * @param {HTMLElement} listItem
     * @param {number} portionGrams
     * @returns {boolean} whether a matching in-stock variant was found and `selectItem` ran
     */
    const preselectVariantByPortionGrams = (listItem, portionGrams) => {
      const dropdown = findVariantDropdown(listItem);
      if (!dropdown) return false;

      const idx = findMatchingPortionVariantIndex(dropdown, portionGrams);
      if (idx < 0) return false;

      // Prevent scroll-jumps caused by the dropdown implementation focusing the button.
      // We keep `custom-variant-select-dropdown.js` untouched by temporarily overriding focus()
      // to call `focus({ preventScroll: true })` when supported.
      const button = dropdown.querySelector('.dropdown-button');
      const canPreventScrollFocus = button instanceof HTMLElement && typeof button.focus === 'function';
      const originalFocus = canPreventScrollFocus ? button.focus.bind(button) : null;
      if (canPreventScrollFocus && originalFocus) {
        try {
          /** @type {any} */ (button).focus = (/** @type {any} */ options) => {
            // Prefer preventScroll when supported; ignore if the browser rejects the options object.
            try { originalFocus({ preventScroll: true }); } catch { originalFocus(); }
          };
        } catch {
          /* ignore */
        }
      }

      // Prefer calling the web component method if present (most reliable).
      const anyDropdown = /** @type {any} */ (dropdown);
      let applied = false;
      try {
        if (typeof anyDropdown.selectItem === 'function') {
          try { anyDropdown.selectItem(idx); } catch { /* ignore */ }
          applied = true;
        }
      } finally {
        if (canPreventScrollFocus && originalFocus && button instanceof HTMLElement) {
          try { /** @type {any} */ (button).focus = originalFocus; } catch { /* ignore */ }
        }
      }
      return applied;
    };

    items.forEach((el) => {
      if (!chosenSet.has(el)) {
        delete el.dataset.ecommSelectedDietToken;
        delete el.dataset.ecommSelectedPortionGrams;
        return;
      }
      const token = String(chosenDietMap.get(el) || '').trim().toLowerCase();
      if (token) el.dataset.ecommSelectedDietToken = token;
      else delete el.dataset.ecommSelectedDietToken;

      const portionGrams = chosenPortionGramsMap.get(el);
      if (Number.isFinite(Number(portionGrams)) && Number(portionGrams) > 0) {
        el.dataset.ecommSelectedPortionGrams = String(Number(portionGrams));
        preselectVariantByPortionGrams(el, Number(portionGrams));
      } else {
        delete el.dataset.ecommSelectedPortionGrams;
      }
    });

    const orderedRest = utils.sortElementsByPreferredDiet(rest, preferredDietOrderTokens);

    const restPortion =
      Number.isFinite(Number(profilePortionGramsForRest)) && Number(profilePortionGramsForRest) > 0
        ? Number(profilePortionGramsForRest)
        : null;
    if (restPortion !== null) {
      orderedRest.forEach((/** @type {HTMLElement} */ el) => {
        if (preselectVariantByPortionGrams(el, restPortion)) {
          el.dataset.ecommSelectedPortionGrams = String(restPortion);
        } else {
          delete el.dataset.ecommSelectedPortionGrams;
        }
      });
    }

    const orderedItems = [...chosen, ...orderedRest];
    const pickedProductIdSet = new Set(
      chosen
        .map((el) => String(el.getAttribute('data-product-id') || '').trim())
        .filter(Boolean)
    );

    return { orderedItems, pickedProductIdSet };
  };

  /**
   * Apply (or clear) the recommendation + diet badges on list items.
   * Shared by guest + logged-in controllers.
   *
   * Requirements on each listItem:
   * - `data-ecomm-recommendation-picked="recommendedProduct"` to be considered "picked"
   * - `data-ecomm-diets` must exist (JSON) if you want the diet badge to show conditionally
   *
   * @param {HTMLElement[]} listItems
   * @param {string|string[]} selectedDietLabels
   */
  utils.applyRecommendationBadges = function applyRecommendationBadges(listItems, selectedDietLabels) {
    const dietPriorityLabels = ['Zoutarm', 'Koolhydraatarm', 'Vegetarisch', 'Caloriearm', 'Eiwitrijk', 'Natriumarm'];
    const dietPriorityTokens = dietPriorityLabels.map((x) => String(x).trim().toLowerCase());
    /** @type {Record<string, string>} */
    const dietTokenToLabel = {};
    dietPriorityTokens.forEach((token, i) => { dietTokenToLabel[token] = dietPriorityLabels[i] || token; });

    const selectedDietTokens = Array.from(
      new Set(
        (Array.isArray(selectedDietLabels) ? selectedDietLabels : [selectedDietLabels])
          .map((x) => String(x || '').trim().toLowerCase())
          .filter(Boolean)
      )
    );
    /** @type {any} */
    const algorithmApi = windowAny.EcommRecommendationsAlgorithm;

    listItems
      .filter((el) => el instanceof HTMLElement)
      .forEach((listItem) => {
        const isPicked = listItem.dataset.ecommRecommendationPicked === 'recommendedProduct';
        const forcedDietToken = String(listItem.dataset.ecommSelectedDietToken || '').trim().toLowerCase();

        const recommendationsBadgesContainer = listItem.querySelector('[data-ecomm-recommendations-badges]');
        if (recommendationsBadgesContainer instanceof HTMLElement) {
          recommendationsBadgesContainer.classList.toggle('no-disp', !isPicked);
        }

        // Find the existing diet badge text element from the Liquid snippet
        const dietBadgeTextElement = listItem.querySelector('[data-ecomm-diet-badge-text]');
        if (!(dietBadgeTextElement instanceof HTMLElement)) return;

        // Find matching diet tokens
        /** @type {string[]} */
        let chosenTokens = [];
        let productMatchesSelectedDiets = false;
        try {
          const rawDiets = listItem.getAttribute('data-ecomm-diets');
          let parsedDiets = null;
          try { parsedDiets = rawDiets ? JSON.parse(rawDiets) : null; } catch { parsedDiets = rawDiets; }
          if (algorithmApi && typeof algorithmApi.convertMetafieldValuesToNormalizedTokens === 'function') {
            const dietTokens = algorithmApi.convertMetafieldValuesToNormalizedTokens(parsedDiets);
            productMatchesSelectedDiets = selectedDietTokens.length > 0 && 
              dietTokens.some((/** @type {string} */ token) => selectedDietTokens.includes(token));
            
            const matchingTokens = selectedDietTokens.length > 0 && dietTokens.length > 0
              ? dietTokens.filter((/** @type {string} */ token) => selectedDietTokens.includes(token))
              : [];
            
            if (forcedDietToken && dietTokens.includes(forcedDietToken)) {
              chosenTokens = [forcedDietToken];
              matchingTokens.forEach((/** @type {string} */ token) => {
                if (token !== forcedDietToken && !chosenTokens.includes(token)) {
                  chosenTokens.push(token);
                }
              });
            } else if (matchingTokens.length > 0) {
              chosenTokens = dietPriorityTokens.filter((token) => matchingTokens.includes(token));
              matchingTokens.forEach((/** @type {string} */ token) => {
                if (!chosenTokens.includes(token)) {
                  chosenTokens.push(token);
                }
              });
            }
          }
        } catch {
          chosenTokens = [];
        }

        // Show badge if product matches selected diets or has forced token
        const shouldShowBadge = chosenTokens.length > 0 && (isPicked || productMatchesSelectedDiets || forcedDietToken);
        
        if (shouldShowBadge && (selectedDietTokens.length > 0 || forcedDietToken)) {
          // Get the highest priority matching token label
          const primaryToken = chosenTokens[0] || '';
          const badgeLabel = primaryToken ? (dietTokenToLabel[primaryToken] || primaryToken) : '';
          dietBadgeTextElement.textContent = badgeLabel;
        } else {
          // Clear the badge text (CSS will hide it when empty)
          dietBadgeTextElement.textContent = '';
        }
      });
  };

})();
